import React, { useState, useEffect, useMemo } from "react";
import { Icon } from "@iconify/react";
import Button from "../Button";
import { Typography } from "../Typography";

const getStudentName = (idea) => {
  if (!idea) return "";
  return (
    idea.student_name ||
    idea.studentName ||
    idea.submitter?.name ||
    idea.user?.name ||
    idea.created_by_name ||
    ""
  ).trim();
};

const TaskFilterModal = ({ isOpen, onClose, onApply, filters: initialFilters = {}, ideas: ideasProp = [] }) => {
  const [filters, setFilters] = useState({
    status: initialFilters.status || "",
    priority: initialFilters.priority || "",
    idea: initialFilters.idea || "",
    student: initialFilters.student || "",
  });

  const ideas = useMemo(
    () =>
      (ideasProp || []).map((idea) => {
        const ideaName = idea.name || idea.title || idea.idea_name || idea.startupName || "";
        return { value: ideaName, label: ideaName };
      }),
    [ideasProp]
  );

  const uniqueStudents = useMemo(() => {
    const names = [...new Set((ideasProp || []).map(getStudentName).filter(Boolean))];
    return names.map((name) => ({ value: name, label: name }));
  }, [ideasProp]);

  const statuses = [
    { value: "Pending", label: "Pending" },
    { value: "In Progress", label: "In Progress" },
    { value: "Completed", label: "Completed" },
  ];

  const priorities = [
    { value: "High", label: "High" },
    { value: "Medium", label: "Medium" },
    { value: "Low", label: "Low" },
  ];

  useEffect(() => {
    if (isOpen) {
      setFilters({
        status: initialFilters.status || "",
        priority: initialFilters.priority || "",
        idea: initialFilters.idea || "",
        student: initialFilters.student || "",
      });
    }
  }, [isOpen, initialFilters]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClear = () => {
    const clearedFilters = {
      status: "",
      priority: "",
      idea: "",
      student: "",
    };
    setFilters(clearedFilters);
    if (onApply) {
      onApply(clearedFilters);
    }
  };

  const handleApply = () => {
    if (onApply) {
      onApply(filters);
    }
    onClose();
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some((value) => value !== "");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 inline-flex items-center justify-center rounded-lg bg-purple-100">
              <Icon icon="lucide:filter" className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <Typography as="h2" className="text-xl font-bold text-primary-900">
                Filters
              </Typography>
              <Typography as="p" className="text-sm text-tertiary-600 mt-1">
                Filter tasks by status, priority, idea, or student.
              </Typography>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <Icon icon="lucide:x" className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex flex-col gap-6">
            {/* Status Filter */}
            <div className="text-left">
              <label className="text-sm font-medium text-slate-700 block mb-1 text-left">
                Status
              </label>
              <div className="relative">
                <select
                  name="status"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white pl-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none text-left"
                >
                  <option value="">All Statuses</option>
                  {statuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
                <Icon
                  icon="lucide:chevron-down"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                />
              </div>
            </div>

            {/* Priority Filter */}
            <div className="text-left">
              <label className="text-sm font-medium text-slate-700 block mb-1 text-left">
                Priority
              </label>
              <div className="relative">
                <select
                  name="priority"
                  value={filters.priority}
                  onChange={(e) => handleFilterChange("priority", e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white pl-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none text-left"
                >
                  <option value="">All Priorities</option>
                  {priorities.map((priority) => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
                <Icon
                  icon="lucide:chevron-down"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                />
              </div>
            </div>

            {/* Idea Filter */}
            <div className="text-left">
              <label className="text-sm font-medium text-slate-700 block mb-1 text-left">
                Idea
              </label>
              <div className="relative">
                <select
                  name="idea"
                  value={filters.idea}
                  onChange={(e) => handleFilterChange("idea", e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white pl-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none text-left"
                >
                  <option value="">All Ideas</option>
                  {ideas.map((idea) => (
                    <option key={idea.value} value={idea.value}>
                      {idea.label}
                    </option>
                  ))}
                </select>
                <Icon
                  icon="lucide:chevron-down"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                />
              </div>
            </div>

            {/* Student Filter */}
            <div className="text-left">
              <label className="text-sm font-medium text-slate-700 block mb-1 text-left">
                Student
              </label>
              <div className="relative">
                <select
                  name="student"
                  value={filters.student}
                  onChange={(e) => handleFilterChange("student", e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white pl-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none text-left"
                >
                  <option value="">All Students</option>
                  {uniqueStudents.map((student) => (
                    <option key={student.value} value={student.value}>
                      {student.label}
                    </option>
                  ))}
                </select>
                <Icon
                  icon="lucide:chevron-down"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-slate-200 bg-slate-50">
          <Button
            variant="secondary"
            onClick={handleClear}
            disabled={!hasActiveFilters()}
            startIcon={<Icon icon="lucide:x" className="h-4 w-4" />}
          >
            Clear Filters
          </Button>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleApply}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskFilterModal;

